import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { ImageBox } from '../../ui/ImageBox';
import { TireTrack } from '../TireTrack';
import { IMAGES } from '../../../data/images';

/**
 * Three facts, each one confirmed rather than inferred: the Bike Night
 * sign in the café's own photographs plus the client's confirmation that
 * it runs all year, the car park the client confirmed 2026-09-12, and the
 * range of machines in those same pictures.
 *
 * No number is given for the parking. A figure is what someone plans a
 * club run around, and none is on record. See D46.
 */
const FACTS = [{
  value: 'Fri',
  label: 'Bike night, all year round'
}, {
  value: 'Free',
  label: 'Parking, and plenty of it'
}, {
  value: 'All',
  label: 'Bikes and riders welcome'
}];

/**
 * Asymmetric by intent: the prose carries the weight and the imagery
 * supports it, so the two columns are deliberately unequal rather than a
 * matched pair of boxes.
 */
export function AboutSection() {
  return <section id="about" aria-labelledby="about-heading" className="relative overflow-hidden bg-cream py-16 sm:py-24" data-id="element-201" data-mp="2pi5wtga0wppy">
      <TireTrack variant="curve-left" className="absolute -left-20 top-10 h-[250px] w-[500px]" opacity={0.04} data-id="element-202" data-mp="2kkmh0bxog4vw" />
      <TireTrack variant="curve-right" className="absolute -right-16 bottom-20 h-[200px] w-[400px] rotate-12" opacity={0.03} data-id="element-203" data-mp="2bin33shylgx6" />

      <div className="relative mx-auto max-w-container px-gutter-sm sm:px-gutter-md lg:px-gutter-lg" data-id="element-204" data-mp="338rj7eyorhsl">
        <div className="grid gap-12 lg:grid-cols-[1.15fr_1fr] lg:items-center lg:gap-16" data-id="element-205" data-mp="2yi1c9xam36vs">
          <div data-id="element-206" data-mp="2yfonp1acly9r">
            <p className="mb-5 border-l-4 border-rust pl-3 text-sm font-black uppercase tracking-[0.2em] text-rust" data-id="element-207" data-mp="2ugpj5hsj43iu">
              The meeting point
            </p>

            <h2 id="about-heading" className="font-bebas text-5xl leading-[0.9] tracking-wide text-ink sm:text-6xl" data-id="element-208" data-mp="3r0gg78mgcufv">
              A place for
              <br data-id="element-209" data-mp="1kalfbwb881os" />
              <span className="text-moss" data-id="element-210" data-mp="1kc6ano5d7onp">bikers and locals.</span>
            </h2>

            <p className="mt-6 max-w-prose text-lg leading-relaxed text-ink/85" data-id="element-211" data-mp="1p8elipcj4vp2">
              A gritstone café on the hill road at Brackenfield, with a
              terrace and a raised deck that look clean across the valley.
              Whether you're a rider after decent coffee before heading up
              into the Peak District, or a local after a proper plate of
              food, there's a place for you here.
            </p>
            {/* Rewritten once the interior photographs arrived. It is not
                one room but half a dozen, and the veteran motorcycles up
                on the walls are the thing everybody actually photographs. */}
            <p className="mt-4 max-w-prose text-lg leading-relaxed text-ink/85" data-id="element-212" data-mp="2m0iag17rmlot">
              Inside is half a dozen small rooms of stone walls, low beams
              and thirty years of collecting: veteran motorcycles up on the
              walls, enamel petrol signs, race photographs and gig posters,
              a Shell pump by the door and guitars made out of bike chain
              along the front of the bar.
            </p>
            <p className="mt-4 max-w-prose text-lg leading-relaxed text-ink/85" data-id="element-213" data-mp="p32sppjkonk7">
              Find a fireside chair in the snug, a stool at the counter or a
              table under a motorcycle. The chess is always set up.
            </p>

            {/* Label and value pairs, so a dl rather than a div grid. */}
            <dl className="mt-10 grid gap-6 sm:grid-cols-3" data-id="element-214" data-mp="17zu5dg4m04fa">
              {FACTS.map(fact => <div key={fact.label} className="border-l-4 border-moss pl-4" data-id="element-215" data-mp="130rb8qq63im3">
                  <dd className="font-bebas text-4xl leading-none tracking-wide text-ink" data-id="element-216" data-mp="12yog5sa5qzai">
                    {fact.value}
                  </dd>
                  <dt className="mt-1.5 text-sm font-bold uppercase tracking-wide text-ink-muted" data-id="element-217" data-mp="34sxh204exlsd">
                    {fact.label}
                  </dt>
                </div>)}
            </dl>

            <Link to="/visit" className="group mt-8 inline-flex items-center gap-2 text-sm font-bold uppercase tracking-wide text-rust underline decoration-rust/40 underline-offset-4 transition-colors duration-200 ease-out-expo hover:decoration-rust focus:outline-none focus:ring-2 focus:ring-rust focus:ring-offset-2" data-id="element-218" data-mp="3b7csjepz4u8d">
              Hours, parking and how to find us
              <ArrowRight className="h-4 w-4 transition-transform duration-200 ease-out-expo group-hover:translate-x-1 motion-reduce:transform-none" aria-hidden="true" data-id="element-219" data-mp="2sio9yzeyr0zr" />
            </Link>
          </div>

          <div className="grid grid-cols-2 gap-4" data-id="element-220" data-mp="24sbv14law3zo">
            <ImageBox src={IMAGES.theBar.src} alt={IMAGES.theBar.alt} ratio="4/5" className="rounded-container shadow-xl" data-id="element-221" data-mp="24rsloc9punx9" />
            <ImageBox src={IMAGES.deckView.src} alt={IMAGES.deckView.alt} ratio="4/5" className="mt-10 rounded-container shadow-xl" data-id="element-222" data-mp="quze6u95x2o4" />
          </div>
        </div>
      </div>
    </section>;
}